import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../hooks/useCart";
import { ProductQuantityCalculator } from "../../utils/ProductQuantityCalculator";
import QuantityInput from "./QuantityInput";
import { formatCurrency } from "../../utils/formatters";
import ImageComingSoon from "../common/ImageComingSoon";
import StoreOrderInfo from "../common/StoreOrderInfo";
import { useMedRecFilter } from "../../context/MedRecFilterContext";
import { appInsights } from "../../appInsights.js";
import { useStoreCredits } from "../../hooks/useStoreCredits";
import { usableCreditTotal } from "../../utils/checkoutCredits";

// Cannabis icon for Med/Rec badges
import cannabisIcon from "../../assets/icons/reccannabis.png";

const CartOffcanvas = () => {
  const navigate = useNavigate();

  // Med/Rec filter context - only show badge when store supports both
  const { showFilter: showMedRecBadge } = useMedRecFilter();

  const {
    cartItems,
    clearCart,
    getCartOriginalSubtotal,
    getCartFeeTotal,
    getCartTotal,
    getCartItemCount,
    getCartSavings,
    updateCartItemQuantity,
    removeFromCart,
    isLoading,
    isUpdatingCart,
    error,
    validationResults,
    hasValidationErrors,
    hasStore,
    canUseCart,
    brandTotals,
    creditableCartTotal,
  } = useCart();

  // How much of the store's credits could go on this cart, to point the customer at checkout.
  const { enabled: creditsEnabled, availableCredits, stashHouseBrands } = useStoreCredits();
  const usableCredits = useMemo(
    () =>
      creditsEnabled && availableCredits.length > 0
        ? usableCreditTotal(availableCredits, creditableCartTotal, brandTotals, stashHouseBrands.map((b) => b.brandId))
        : 0,
    [creditsEnabled, availableCredits, creditableCartTotal, brandTotals, stashHouseBrands],
  );

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const cartHeaderRef = useRef();
  const cartFooterRef = useRef();
  const [cartBodyHeight, setCartBodyHeight] = useState({ height: "400px" });
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Handle navigation with offcanvas close
  const handleNavigate = (path) => {
    // Close the offcanvas using multiple methods for reliability
    const offcanvasElement = document.getElementById('cartOffcanvas');
    if (offcanvasElement) {
      // Method 1: Try Bootstrap's Offcanvas API
      const bsOffcanvas = window.bootstrap?.Offcanvas?.getInstance(offcanvasElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      } else {
        // Method 2: Click the close button
        const closeBtn = offcanvasElement.querySelector('[data-bs-dismiss="offcanvas"]');
        if (closeBtn) {
          closeBtn.click();
        } else {
          // Method 3: Remove show class and backdrop manually
          offcanvasElement.classList.remove('show');
          document.body.classList.remove('offcanvas-backdrop');
          const backdrop = document.querySelector('.offcanvas-backdrop');
          if (backdrop) {
            backdrop.remove();
          }
        }
      }
    }
    // Navigate to the path
    navigate(path);
  };

  // Update window size
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate cart body height
  useEffect(() => {
    if (cartHeaderRef.current && cartFooterRef) {
      const headerHeight = cartHeaderRef.current.getBoundingClientRect().height;
      const footerHeight = cartFooterRef.current.getBoundingClientRect().height;
      const availableHeight = windowSize.height - headerHeight - footerHeight;
      setCartBodyHeight({ height: `${Math.max(400, availableHeight)}px` });
    }
  }, [windowSize, cartHeaderRef.current]);

  const handleRemoveItem = async (item) => {
    if (appInsights) {
      appInsights.trackEvent({ name: 'CartItemDeleted' }, {
        productId: String(item.productSkuVariantID || item.id || ''),
        productName: item.productName || '',
        brandName: item.brandName || '',
        unitQuantity: item.unitQuantity,
        source: 'cartSidebar',
      });
    }
    try {
      await removeFromCart(item.id);
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  const handleUpdateQuantity = async (item, newQuantity) => {
    try {
      await updateCartItemQuantity(item.id, newQuantity);
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const handleClearCart = () => setShowClearConfirm(true);

  const confirmClearCart = async () => {
    setShowClearConfirm(false);
    try {
      await clearCart();
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  // Transform cart items for display with quantity calculator
  const displayItems = useMemo(() => {
    return cartItems.map((item) => {
      // Create a product object for the calculator - include pricing fields
      const productData = {
        id: item.productSkuVariantID,
        unitsPerCase: item.productSkuVariant?.unitsPerCase || 1,
        optSellByTypeID: item.productSkuVariant?.optSellByTypeID || 1,
        unitAvailable: item.productSkuVariant?.unitAvailable || 0,
        // Pricing fields - check both item level (from cart) and productSkuVariant level.
        // Prefer the product's current frontline price (productSkuVariant) over the stored
        // line price: item.unitPrice already has any discount baked in at add-time, which would
        // hide the strikethrough. Letting the calculator derive the discount/special off the
        // frontline keeps the effective price the same while restoring the crossed-out original.
        unitPrice: item.productSkuVariant?.unitPrice || item.unitPrice || 0,
        discountUnitPrice: item.discountUnitPrice || item.productSkuVariant?.discountUnitPrice || null,
        singleUnitFee: item.singleUnitFee || item.productSkuVariant?.singleUnitFee || 0,
        productSkuVariantMOQPricing: item.productSkuVariantMOQPricing || item.productSkuVariant?.productSkuVariantMOQPricing || [],
      };

      // Debug: Log pricing data
      console.log('CartOffcanvas - Item pricing data:', {
        productId: item.productSkuVariantID,
        unitQuantity: item.unitQuantity,
        unitPrice: productData.unitPrice,
        discountUnitPrice: productData.discountUnitPrice,
        moqPricing: productData.productSkuVariantMOQPricing,
        fromProductSkuVariant: item.productSkuVariant?.productSkuVariantMOQPricing
      });

      const calculator = new ProductQuantityCalculator(productData);
      const displayQuantity = calculator.unitsToDisplay(item.unitQuantity || 0);

      // Find validation for this item
      const validation = validationResults?.find((v) => v.itemId === item.id);

      // Standardized image URL extraction
      const getImageUrl = (item) => {
        // First, try the processed images array (from productService.getProducts)
        if (
          item.productSkuVariant?.images &&
          item.productSkuVariant.images.length > 0
        ) {
          return item.productSkuVariant.images[0].fullPath;
        }

        // Then try the expanded database structure (from cart database)
        if (item.productSkuVariant?.productSKUVariantImages?.length > 0) {
          const firstImage = item.productSkuVariant.productSKUVariantImages[0];
          if (firstImage?.image?.fullPath) {
            return `https://stashhouseimages.blob.core.windows.net/${firstImage.image.fullPath}`;
          }
        }

        // Return null when no image available
        return null;
      };

      return {
        ...item,
        calculator,
        displayQuantity,
        validation,
        hasValidationError: validation && !validation.isValid,
        productName: item.productSkuVariant?.name || "Unknown Product",
        brandName: item.productSkuVariant?.brand?.name,
        flavor: item.productSkuVariant?.flavor,
        imageUrl: getImageUrl(item),
        unitsPerCase: productData.unitsPerCase,
        optSellByTypeID: productData.optSellByTypeID,
        canSellByUnit: calculator.canSellByUnit,
        canSellByCase: calculator.canSellByCase,
        unitAvailable: productData.unitAvailable,
        singleUnitFee: productData.singleUnitFee,
        isMed: item.productSkuVariant?.isMed || false,
        isRec: item.productSkuVariant?.isRec || false,
      };
    });
  }, [cartItems, validationResults]);

  // Show message if user doesn't have store selected
  if (!canUseCart) {
    return (
      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="cartOffcanvas"
        aria-labelledby="cartOffcanvasLabel"
        style={{ width: "90%", maxWidth: "1200px" }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title fw-bold" id="cartOffcanvasLabel">
            YOUR CART
          </h5>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>
        </div>

        <div className="offcanvas-body d-flex align-items-center justify-content-center">
          <div className="text-center">
            <div className="mb-3">
              <i className="bi bi-shop display-4 text-muted"></i>
            </div>
            <h6 className="text-muted">Select a Store First</h6>
            <p className="small text-muted mb-3">
              Please select a store before viewing your cart
            </p>
            <Link
              to="/account"
              className="btn btn-primary"
              data-bs-dismiss="offcanvas"
            >
              Select Store
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      id="cartOffcanvas"
      aria-labelledby="cartOffcanvasLabel"
      style={{ width: "90%", maxWidth: "1200px" }} // Expanded width
    >
      {/* Header */}
      <div className="offcanvas-header border-bottom justify-content-between" ref={cartHeaderRef}>
        <h5 className="offcanvas-title fw-bold" id="cartOffcanvasLabel">
          YOUR CART ({getCartItemCount()})
        </h5>
        <div className="d-flex align-items-center gap-3">
          {cartItems.length > 0 && (
            <button
              type="button"
              className="btn btn-link text-danger p-0 border-0 bg-transparent text-decoration-none"
              onClick={handleClearCart}
              disabled={isLoading}
            >
              <i className="bi bi-trash me-1"></i>
              Clear All
            </button>
          )}
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger m-3 mb-0" role="alert">
          <div className="d-flex justify-content-between align-items-center">
            <span>
              {typeof error === "string"
                ? error
                : error.message || "An error occurred"}
            </span>
            <button type="button" className="btn-close btn-close-sm"></button>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="offcanvas-body p-0">
        <div className="h-100">
          {/* Cart Items - Full Width Table Layout */}
          <div className="border-end">
            <div className="px-3 pt-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <h6 className="mb-0">Cart Items</h6>
                {cartItems.length > 0 && (
                  <small className="text-muted">
                    ({cartItems.length} items)
                  </small>
                )}
                {isUpdatingCart && (
                  <div className="ms-2">
                    <div
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">Updating...</span>
                    </div>
                  </div>
                )}
              </div>

              {cartItems.length > 0 ? (
                <div style={cartBodyHeight} className="cart-items-container border border-dark">
                  {/* Global Inventory Warning */}
                  {hasValidationErrors && (
                    <div className="alert alert-danger small py-2 mb-3">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Some items have inventory issues. Please review quantities
                      below.
                    </div>
                  )}

                  {/* Table Layout - Similar to QuickOrder */}
                  <div className="table-responsive">
                    <table className="table align-middle">
                      <thead className="table-light d-none">
                        <tr>
                          <th style={{ width: "60px" }}>Image</th>
                          <th>Product</th>
                          <th style={{ width: "140px" }}>Price</th>
                          <th style={{ width: "250px" }}>Quantity</th>
                          <th style={{ width: "120px" }}>Total</th>
                          <th style={{ width: "60px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayItems.map((item) => (
                          <CartItemRow
                            key={item.id}
                            item={item}
                            onUpdateQuantity={handleUpdateQuantity}
                            onRemoveItem={handleRemoveItem}
                            isUpdating={isUpdatingCart}
                            showMedRecBadge={showMedRecBadge}
                            isMobile={windowSize.width <= 768}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <i className="bi bi-cart3 display-4 text-muted"></i>
                  </div>
                  <h6 className="text-muted">Your cart is empty</h6>
                  <p className="small text-muted mb-3">
                    Add some products to get started
                  </p>
                  <Link
                    to="/categories"
                    className="btn btn-primary"
                    data-bs-dismiss="offcanvas"
                  >
                    Browse Products
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Cart Summary */}
          <div className="container-fluid py-3 px-3" ref={cartFooterRef}>
            <div className="row justify-content-end">
              {/* Store Order Info - Sales Rep, Cutoff, Delivery Days */}
              <div className="col me-3">
                <StoreOrderInfo variant="default" />
              </div>

              <div className="col-auto">
                <div className="card border-0">

                    <div className="card-body p-0">
                      <div className="border-dark border mb-3">
                        <table className="table align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "40px" }}></th>
                              <th style={{ width: "225px" }}>ORDER SUMMARY</th>
                              <th style={{ width: "120px" }}></th>
                              <th style={{ width: "70px" }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td></td>
                              <td className="">SUBTOTAL</td>
                              <td className="text-end">
                                <span className="fw-medium">
                                    {formatCurrency(getCartOriginalSubtotal())}
                                  </span>
                              </td>
                              <td></td>
                            </tr>
                            <tr>
                              <td></td>
                              <td className="">FEES</td>
                              <td className="text-end">
                                <span className="fw-medium">
                                    {formatCurrency(getCartFeeTotal())}
                                  </span>
                              </td>
                              <td></td>
                            </tr>
                            <tr>
                              <td></td>
                              <td className="">DELIVERY FEE</td>
                              <td className="text-end">
                                <span className="fw-medium">
                                    FREE
                                  </span>
                              </td>
                              <td></td>
                            </tr>
                            {getCartSavings() > 0 && (
                              <tr>
                                <td></td>
                                <td className="text-success">
                                  <i className="bi bi-tag-fill me-1"></i>
                                  YOUR SAVINGS
                                </td>
                                <td className="text-end">
                                  <span className="fw-medium text-success">
                                    -{formatCurrency(getCartSavings())}
                                  </span>
                                </td>
                                <td></td>
                              </tr>
                            )}
                            <tr>
                              <td></td>
                              <th className="">ORDER TOTAL</th>
                              <td className="text-end">
                                <span className="fw-bold text-stash-accent fs-5">
                                    {formatCurrency(getCartTotal())}
                                  </span>
                              </td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      {usableCredits > 0 && (
                        <div className="d-flex align-items-start border border-stash-accent bg-stash-accent-light rounded py-2 px-3 mb-3">
                          <i className="bi bi-wallet2 me-2 mt-1 text-stash-accent"></i>
                          <span>
                            You have <strong>{formatCurrency(usableCredits)}</strong> in credits you can use on this
                            order. Apply them at checkout.
                          </span>
                        </div>
                      )}
                      <div className="d-grid gap-2">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => handleNavigate('/checkout')}
                            >
                              {isLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" />
                                  Loading...
                                </>
                              ) : (
                                "Continue to Checkout"
                              )}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => handleNavigate('/categories')}
                            >
                              Continue Shopping
                            </button>
                          </div>
                    </div>
                  </div>
              
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .cart-items-container {
          max-height: 60vh;
          overflow-y: auto;
          padding-right: 8px;
        }

        .cart-items-container::-webkit-scrollbar {
          width: 6px;
        }

        .cart-items-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .cart-items-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .cart-items-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        .cart-item-row--validation-error {
          background-color: rgba(255, 193, 7, 0.1) !important;
        }

        @media (max-width: 768px) {
          .offcanvas {
            width: 100% !important;
            max-width: none !important;
          }

          .table-responsive {
            font-size: 0.875rem;
          }

          th,
          td {
            padding: 0.5rem 0.25rem !important;
          }
        }
      `}</style>
    </div>

    {/* Clear cart confirmation */}
    {showClearConfirm && (
      <>
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ zIndex: 1065 }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle text-danger me-2"></i>
                  Clear cart?
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowClearConfirm(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  Are you sure you want to remove all items from your cart? This
                  can&apos;t be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowClearConfirm(false)}
                >
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmClearCart}>
                  Clear Cart
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
      </>
    )}
    </>
  );
};

// Separate CartItemRow Component for cleaner organization
const CartItemRow = ({ item, onUpdateQuantity, onRemoveItem, isUpdating, showMedRecBadge, isMobile }) => {
  const handleCaseChange = (newCases) => {
    const newTotal = item.calculator.displayToUnits(
      newCases,
      item.displayQuantity.units,
    );
    onUpdateQuantity(item, newTotal);
  };

  const handleUnitChange = (newUnits) => {
    const newTotal = item.calculator.displayToUnits(
      item.displayQuantity.cases,
      newUnits,
    );
    onUpdateQuantity(item, newTotal);
  };

  const quickIncrement = () => {
    const newTotal = item.calculator.smartIncrement(item.unitQuantity);
    if (appInsights) {
      appInsights.trackEvent({ name: 'QuantityChanged' }, {
        direction: 'increment',
        quantityType: 'total',
        newValue: newTotal,
        productId: String(item.productSkuVariantID || item.id || ''),
        productName: item.productName || '',
        brandName: item.brandName || '',
        source: 'quickButton',
      });
    }
    onUpdateQuantity(item, newTotal);
  };

  const quickDecrement = () => {
    const newTotal = item.calculator.smartDecrement(item.unitQuantity);
    if (appInsights) {
      appInsights.trackEvent({ name: 'QuantityChanged' }, {
        direction: 'decrement',
        quantityType: 'total',
        newValue: newTotal,
        productId: String(item.productSkuVariantID || item.id || ''),
        productName: item.productName || '',
        brandName: item.brandName || '',
        source: 'quickButton',
      });
    }
    onUpdateQuantity(item, newTotal);
  };

  // Calculate MOQ-aware pricing using the calculator
  const priceInfo = useMemo(() => {
    if (item.calculator) {
      return item.calculator.calculatePrice(item.unitQuantity || 0);
    }
    // Fallback calculation if no calculator
    const lineSubtotal = (item.unitQuantity || 0) * (item.unitPrice || 0);
    const lineFees = (item.unitQuantity || 0) < item.unitsPerCase
      ? parseFloat(item.singleUnitFee || 0) * (item.unitQuantity || 0)
      : 0;
    return {
      unitPrice: item.unitPrice || 0,
      originalUnitPrice: item.unitPrice || 0,
      totalPrice: lineSubtotal,
      fees: lineFees,
      grandTotal: lineSubtotal + lineFees,
      hasSavings: false,
      isMOQPrice: false,
      savingsPercent: 0,
      totalSavings: 0
    };
  }, [item.calculator, item.unitQuantity, item.unitPrice, item.unitsPerCase, item.singleUnitFee]);

  const lineFees = priceInfo.fees;
  const lineTotal = priceInfo.grandTotal;
  const effectiveUnitPrice = priceInfo.unitPrice;

  return (
    <tr
      className={`cart-item-row border-dark-subtle ${item.hasValidationError ? "cart-item-row--validation-error" : ""}`}
    >
      {/* Product Image */}
      <td>
        <div
          className="ratio ratio-1x1 rounded overflow-hidden bg-light"
          style={{ width: "48px", height: "48px" }}
        >
          {item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt={item.productName}
              className="object-fit-cover"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <ImageComingSoon style={{ minHeight: "48px" }} />
          )}
        </div>
      </td>

      {/* Product Info */}
      <td>
        <div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <strong
              className="text-truncate"
              style={{ maxWidth: "100%" }}
            >
              {item.productName}
            </strong>
            {/* Med/Rec badge - only show when store supports both types */}
            {showMedRecBadge && (item.isMed || item.isRec) && (
              <span
                className={`badge rounded-pill d-inline-flex align-items-center ${
                  item.isMed ? 'bg-success' : 'bg-purple'
                }`}
                title={item.isMed ? 'Medical Product' : 'Recreational Product'}
                style={{ fontSize: '0.65rem' }}
              >
                <img src={cannabisIcon} alt="" className="med-rec-badge-icon me-1" />
                {item.isMed ? 'Med' : 'Rec'}
              </span>
            )}
          </div>
          {item.brandName && (
            <small className="text-primary d-block">{item.brandName}</small>
          )}
          {item.flavor && (
            <small className="text-muted d-block">{item.flavor}</small>
          )}
          <small className="text-muted d-block">
            {item.unitsPerCase} {item.calculator?.unitLabelPlural?.toLowerCase() || 'units'} / {item.calculator?.caseLabel?.toLowerCase() || 'case'}
          </small>
          {/* Validation Error */}
          {item.hasValidationError && (
            <small className="text-warning d-block">
              <i className="bi bi-exclamation-triangle me-1"></i>
              {item.validation.message}
            </small>
          )}
          <div className="debug small d-none">
            Debug<br/>
             <span className="">
              {item.unitAvailable || 0} avail
            </span>
          </div>
        </div>
      </td>

      {/* Price + Quantity — single unified cell */}
      <td>
        <div className="d-flex flex-column gap-2 mx-auto mx-md-0">

          {/* === Cases Group === */}
          {item.canSellByCase && (
            <div>
              {/* Row: metadata | input | label */}
              <div className={`d-flex ${isMobile ? 'flex-column ' : ''} gap-1`}>
                {/* Case price metadata */}
                <div className={isMobile ? 'mb-1' : 'text-end pe-2'} style={{minWidth: isMobile ? 'auto' : '180px'}}>
                  <small className="text-stash-accent" style={{lineHeight: 1}}>
                    {priceInfo.hasSavings && (
                      <span className="text-decoration-line-through text-muted me-1">
                        {formatCurrency(priceInfo.originalUnitPrice * item.unitsPerCase)}
                      </span>
                    )}
                    <strong>{formatCurrency(effectiveUnitPrice * item.unitsPerCase)}</strong> / {item.calculator?.caseLabel?.toLowerCase() || 'case'}
                    <span className="text-black-50 d-block" style={{fontSize: '0.75em', lineHeight: 1}}>({item.unitsPerCase} {item.calculator?.unitLabelPlural?.toLowerCase() || 'units'} / {item.calculator?.caseLabel?.toLowerCase() || 'case'})</span>
                  </small>
                </div>
                {/* Case input + label */}
                <QuantityInput
                  value={item.displayQuantity.cases}
                  onChange={handleCaseChange}
                  quantityType="cases"
                  product={{
                    id: item.productSkuVariantID,
                    unitsPerCase: item.unitsPerCase,
                    optSellByTypeID: item.optSellByTypeID,
                    unitAvailable: item.unitAvailable,
                  }}
                  currentUnitQuantity={item.displayQuantity.units}
                  size="sm"
                  disabled={isUpdating}
                  isUpdating={isUpdating}
                  label={item.calculator?.caseLabelPlural?.toLowerCase() || 'cases'}
                />
              </div>
            </div>
          )}

          {/* === Units Group === */}
          {item.canSellByUnit && item.unitsPerCase > 1 && (
            <div>
              {/* Row: metadata | input | label */}
              <div className={`d-flex ${isMobile ? 'flex-column ' : ''} gap-1`}>
                {/* Unit price metadata */}
                <div className={isMobile ? 'mb-1' : 'text-end pe-2'} style={{minWidth: isMobile ? 'auto' : '180px'}}>
                  <small className="text-stash-accent" style={{lineHeight: 1}}>
                    {priceInfo.hasSavings && (
                      <span className="text-decoration-line-through text-muted me-1">
                        {formatCurrency(priceInfo.originalUnitPrice)}
                      </span>
                    )}
                    <strong>{formatCurrency(effectiveUnitPrice)}</strong> / {item.calculator?.unitLabel?.toLowerCase() || 'unit'}
                    {parseFloat(item.singleUnitFee || 0) > 0 && (
                      <span className="text-black-50 d-block" style={{fontSize: '0.75em', lineHeight: 1}}>(+{formatCurrency(parseFloat(item.singleUnitFee))} / {item.calculator?.unitLabel?.toLowerCase() || 'unit'} fee)</span>
                    )}
                  </small>
                </div>
                {/* Unit input + label */}
                <QuantityInput
                  value={item.displayQuantity.units}
                  onChange={handleUnitChange}
                  quantityType="units"
                  product={{
                    id: item.productSkuVariantID,
                    unitsPerCase: item.unitsPerCase,
                    optSellByTypeID: item.optSellByTypeID,
                    unitAvailable: item.unitAvailable,
                  }}
                  currentCaseQuantity={item.displayQuantity.cases}
                  size="sm"
                  disabled={isUpdating}
                  isUpdating={isUpdating}
                  label={item.calculator?.unitLabelPlural?.toLowerCase() || 'units'}
                />
              </div>
            </div>
          )}

        </div>
      </td>

      {/* Line Total */}
      <td>
        <div className="text-end">
          {lineFees > 0 && (
            <small className="text-black-50 d-block">
              (incl. {formatCurrency(lineFees)} fees)
            </small>
          )}
          <small className="text-muted d-block">
            {formatCurrency(effectiveUnitPrice)} × {item.unitQuantity}
          </small>
          <strong className="text-stash-accent">{formatCurrency(lineTotal)}</strong>
          {priceInfo.hasSavings && (
            <small className="text-success d-block">
              Save {priceInfo.savingsPercent}%
            </small>
          )}
        </div>
      </td>

      {/* Actions */}
      <td className="text-center">
        <button
          type="button"
          className="btn btn-outline-danger btn-sm"
          onClick={() => onRemoveItem(item)}
          disabled={isUpdating}
          title="Remove from cart"
        >
          <i className="bi bi-trash"></i>
        </button>
      </td>
    </tr>
  );
};

export default CartOffcanvas;
